import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useEffect, useState } from "react";
import LogChangeItem from "./LogChangeItem";
import { ADS_OBJECT_TYPES } from "../../../../../utils/constants";
import CampaignOutlined from "@mui/icons-material/CampaignOutlined";
import { Clapperboard, Images, Layers } from "lucide-react";
import {
  ADS,
  AD_GROUPS,
  ASSETS_GROUPS,
  CAMPAIGN,
  CHANGE,
  CHANGES,
  INSIGHT_APPLIED,
  INSIGHT_DISCARDED,
  IT_WAS_CREATED,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import { startGetItemName } from "../../../../../actions/adsCampaigns";
import { useDispatchApp } from "../../../../../lib/redux";
import InsightsIcon from "@mui/icons-material/Insights";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import {
  GRAY_COLOR_LIGHT,
  INPUT_COLOR_BORDER,
  PRIMARY_COLOR,
} from "../../../../../utils/colors";

const getObjectTypeInfo = ({ objectType, t, hasDiscardedInsight }) => {
  switch (objectType) {
    case ADS_OBJECT_TYPES.campaign:
      return {
        icon: <CampaignOutlined />,
        label: t(CAMPAIGN),
      };
    case ADS_OBJECT_TYPES.adGroup:
      return {
        icon: <Layers size={20} color={PRIMARY_COLOR} />,
        label: t(AD_GROUPS),
      };
    case ADS_OBJECT_TYPES.ad:
      return {
        icon: <Clapperboard size={20} color={PRIMARY_COLOR} />,
        label: t(ADS),
      };
    case ADS_OBJECT_TYPES.assetsGroup:
      return {
        icon: <Images size={20} color={PRIMARY_COLOR} />,
        label: t(ASSETS_GROUPS),
      };
    case ADS_OBJECT_TYPES.insight:
      return {
        icon: hasDiscardedInsight ? <NotInterestedIcon /> : <InsightsIcon />,
        label: hasDiscardedInsight ? t(INSIGHT_DISCARDED) : t(INSIGHT_APPLIED),
      };

    default:
      return {
        icon: <CampaignOutlined />,
        label: t(CAMPAIGN),
        color: "#e3f2fd",
      };
  }
};

const LogChangesGroup = ({
  campaignID,
  objectType,
  changes,
  cachedNameEntities,
  onChangeCachedNameEntities,
  isExpanded = false,
  platform,
  enableReverse = false,
  reverseContext = null,
}) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const { t } = useTranslationApp();
  // Group changes by object ID
  const groupedChanges = changes.reduce((acc, change) => {
    const objectId = change.metadata?.[`${objectType}ID`] || campaignID;
    if (!acc[objectId]) {
      acc[objectId] = {
        changes: [],
        name: "",
        id: objectId,
      };
    }
    acc[objectId].changes.push(change);
    return acc;
  }, {});

  const hasDiscardedInsight = Object.values(groupedChanges).some((group) =>
    group.changes.some((change) => change.newValue?.isDiscarded === true)
  );

  const { icon, label } = getObjectTypeInfo({
    objectType,
    t,
    hasDiscardedInsight,
  });

  const totalChanges = Object.values(groupedChanges).reduce(
    (sum, group) => sum + group.changes.length,
    0
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            borderRadius: 1,
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            backgroundColor: GRAY_COLOR_LIGHT,
            px: 2,
            gap: 1,
          }}
        >
          {icon}
          <Typography variant="body2">{label}</Typography>
        </Box>

        {objectType !== ADS_OBJECT_TYPES.insight && (
          <Typography variant="body2" sx={{ ml: 1 }}>
            {totalChanges} {totalChanges === 1 ? t(CHANGE) : t(CHANGES)}
          </Typography>
        )}

        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          sx={{ ml: "auto", p: 0.5 }}
        >
          {expanded ? (
            <KeyboardArrowUp fontSize="small" />
          ) : (
            <KeyboardArrowDown fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box>
          {Object.entries(groupedChanges).map(([objectID, objectData]) => (
            <LogChangesGroupItem
              key={objectID}
              objectType={objectType}
              objectData={objectData}
              objectID={objectID}
              icon={icon}
              cachedNameEntities={cachedNameEntities}
              onChangeCachedNameEntities={onChangeCachedNameEntities}
              platform={platform}
              enableReverse={enableReverse}
              reverseContext={reverseContext}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const LogChangesGroupItem = ({
  objectType,
  objectData,
  objectID,
  icon,
  cachedNameEntities,
  onChangeCachedNameEntities,
  platform,
  enableReverse = false,
  reverseContext = null,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const getItemName = async ({ objectType, objectID }) => {
    if (cachedNameEntities[objectID]) {
      setName(cachedNameEntities[objectID]);
      setIsLoading(false);
      return;
    }
    if (objectType !== ADS_OBJECT_TYPES.insight) {
      const response = await dispatch(
        startGetItemName({ objectType, objectID })
      );
      if (response.ok) {
        onChangeCachedNameEntities({
          [objectID]: response.data,
        });
        setName(response.data);
      }
    } else {
      setName(t(INSIGHT_APPLIED));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getItemName({ objectType, objectID });
    // eslint-disable-next-line
  }, [objectType, objectID]);

  const finalName = name || objectID;

  const isCreated = objectData.changes.some(
    (change) => change.type === "create"
  );

  return (
    <Box
      key={objectID}
      sx={{
        mb: 2,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: 1,
        p: 2,
        backgroundColor: "rgba(255, 255, 255, 0.05)",
      }}
    >
      {objectType !== ADS_OBJECT_TYPES.insight && (
        <>
          {isLoading ? (
            <Skeleton variant="text" height={24} width={120} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Tooltip title={finalName}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: GRAY_COLOR_LIGHT,
                      border: `1px solid ${INPUT_COLOR_BORDER}`,
                      borderRadius: 1,
                      px: 2,
                    }}
                  >
                    {icon}
                    <Typography variant="body2">{finalName}</Typography>
                  </Box>
                </Tooltip>
              </Typography>
              {isCreated && (
                <Chip
                  label={t(IT_WAS_CREATED)}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    pl: 0.5,
                    backgroundColor: "#48ce4b",
                  }}
                />
              )}
            </Box>
          )}
        </>
      )}

      {objectData.changes.map((change, index) => (
        <LogChangeItem
          key={`${change.field}-${index}`}
          change={change}
          isLast={index === objectData.changes.length - 1}
          cachedNameEntities={cachedNameEntities}
          onChangeCachedNameEntities={onChangeCachedNameEntities}
          objectType={objectType}
          platform={platform}
          enableReverse={enableReverse}
          reverseContext={reverseContext}
        />
      ))}
    </Box>
  );
};

export default LogChangesGroup;
